/**
 * Composable for Luxottica Virtual Mirror (client-only).
 * Initializes VM once and exposes renderMirror for PDP sunglasses.
 */
const VIRTUAL_MIRROR_TARGET_ID = 'target-mirror'

type VirtualMirrorConfig = {
  key: string
  brand: string
  storeId: string
  channel: string
  locale: string
  storeRegion?: string
  storeCompany?: string
  globalStoreId?: string
}

export type VirtualMirrorRenderParams = {
  target?: string
  glasses: Array<{ upc: string }>
}

export type ScreenshotReturns = {
  imgURI: string
  width: number
  height: number
}

let isInitialized = false
let initPromise: Promise<void> | null = null

const ensureInitialized = async (
  config: VirtualMirrorConfig
): Promise<void> => {
  if (isInitialized) return
  if (initPromise) return initPromise

  if (!import.meta.client) return

  const { getLocale } = useI18n()
  const parseLocale = (locale?: string) => {
    if (!locale?.includes('-')) return locale
    const [language, country] = locale.split('-')
    return `${language}-${country.toUpperCase()}`
  }
  const locale = parseLocale(getLocale())

  initPromise = (async () => {
    if (isInitialized) return
    if (!config.key) {
      // console.warn('[useVirtualMirror] Missing VIRTUALMIRROR_KEY')
      return
    }

    const initOptions = {
      key: config.key,
      brand: 'armani',
      storeId: 'armani',
      channel: 'license',
      locale: locale || 'en-WX',
      ...(config.storeRegion && { storeRegion: config.storeRegion }),
      ...(config.storeCompany && { storeCompany: config.storeCompany }),
      ...(config.globalStoreId && { globalStoreId: config.globalStoreId }),
    }
    const { VirtualMirror } = await import('@luxottica/virtual-mirror')

    await VirtualMirror.initialize({ options: initOptions })
    isInitialized = true
  })()

  return initPromise
}

/**
 * Renders the Virtual Mirror into the given target (default: target-mirror).
 * Must be called after the target element is in the DOM (e.g. after nextTick in onMounted).
 */
export const useVirtualMirror = () => {
  const config = useRuntimeConfig().public.virtualMirror as VirtualMirrorConfig

  const renderMirror = async (
    params: VirtualMirrorRenderParams
  ): Promise<void> => {
    if (!import.meta.client || !params.glasses?.length) return

    await ensureInitialized(config)
    if (!isInitialized) return

    const target = params.target ?? VIRTUAL_MIRROR_TARGET_ID
    const { VirtualMirror } = await import('@luxottica/virtual-mirror')

    await VirtualMirror.renderMirror({
      target,
      glasses: params.glasses,
    })
  }

  const closeMirror = async (params: { target: string }): Promise<void> => {
    if (!import.meta.client) return
    try {
      const { VirtualMirror } = await import('@luxottica/virtual-mirror')
      await VirtualMirror.closeMirror({ target: params.target })
    } catch {
      // ignore if mirror was not mounted
    }
  }

  const getScreenshot = async (): Promise<ScreenshotReturns | undefined> => {
    if (!import.meta.client) return undefined
    try {
      const { VirtualMirror } = await import('@luxottica/virtual-mirror')
      const result = await VirtualMirror.getScreenshot()
      if (!result) return undefined
      const screenshot = Array.isArray(result)
        ? (result as ScreenshotReturns[])[0]
        : (result as ScreenshotReturns)
      return screenshot?.imgURI ? screenshot : undefined
    } catch {
      // ignore if mirror was not mounted
      return undefined
    }
  }

  return {
    renderMirror,
    closeMirror,
    getScreenshot,
    targetId: VIRTUAL_MIRROR_TARGET_ID,
  }
}
